import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  createProposedRule,
  getProposedRuleByDraftId,
  updateProposedRule,
} from '../../../../features/state/StateProposedRule-slice';
import { getProposedRuleHistoryByDraftId } from '../../../../features/state/StateProposedRuleHistory-slice';
import { getChapterById } from '../../../../features/state/StateChapter-slice';
import { useNotificationHelper } from '../../../../utilities/helpers/NotificationHelper';
import { ProposedRule } from '../../../../utilities/interfaces/ProposedRule';
import { chapterFileAPI } from '../../../../utilities/api/chapterFileApi';
import { BlobContainer } from '../../../../utilities/enums/BlobContainer';
import { proposedRuleImage } from '../../../../utilities/interfaces/ProposedRuleImage';
import { draftAPI, proposedRuleImageAPI } from '../../../../utilities/api';
import { getAllPublicCms } from '../../../../features/public/publicCms-slice';
import { APISettings } from '../../../../config/ApiSettings';
import { getDraftNoteTypes } from '../../../../features/state/StateNoteType-slice';
// helper functions
import { TextTransform } from '../../../../utilities/helpers/TextTransformHelper';
import { NoteType } from '../../../../utilities/interfaces/NoteType';
import {
  getDraftNotesByDraftId,
  createDraftNote,
  deleteDraftNote,
  updateDraftNote,
  setUpdatedDraftNote,
} from '../../../../features/state/StateDraftNote-slice';
import { DraftNote } from '../../../../utilities/interfaces/DraftNote';
import { Roles } from '../../../../utilities/enums/Roles';
import parse from 'html-react-parser';

export const useDraftEditFormController = (draft: any) => {
  const dispatch = useAppDispatch();
  let proposedRule = useAppSelector((state) => state.proposedRule.proposedRule);
  let proposedRuleHistory = useAppSelector(
    (state) => state.proposedRuleHistory.proposedRuleHistory
  );
  let chapter = useAppSelector((state) => state.chapters.chapter);
  // style guide is stored with the other cmsItems and is in the cms database table
  let cmsItems = useAppSelector((state) => state.publicCms.cmsItems);
  let noteTypes = useAppSelector((state) => state.noteType.draftNoteTypes);
  let draftNotes = useAppSelector((state) => state.draftNote.draftNotes);
  let savingDraft = useAppSelector((state) => state.proposedRule.savingProposedRule);
  const { addNotification } = useNotificationHelper();

  // state
  const [chapterName, setChapterName] = useState<string | undefined>('');
  const [chapterNumber, setChapterNumber] = useState<string | undefined>('');
  // const [selectedChapter] = useState<any>(null);
  const [selectedNoteType, setSelectedNoteType] = useState<NoteType | undefined>();
  const [selectedNote, setSelectedNote] = useState<DraftNote | null | undefined>();
  const [isNoteOpen, setIsNoteOpen] = useState<boolean>(false);
  const [chapterFiles, setChapterFiles] = useState<any>();
  const [isHistoryOpen, setIsHistoryOpen] = useState<boolean>(false);
  const [guidelineDocumentName, setGuidelineDocumentName] = useState<string>('');
  const [noteTypeList, setNoteTypeList] = useState<any[]>([]);

  // helpers
  const { transformUpperCamelCase } = TextTransform();

  // variables
  const baseUrl = new APISettings().getApiURL();
  // #region useEffects and useMemo
  useEffect(() => {
    // console.log('draft - ', draft)
    if (draft) {
      dispatch(getChapterById(draft.chapterId));
      dispatch(getProposedRuleByDraftId(draft.id));
      dispatch(getProposedRuleHistoryByDraftId(draft.id));
      if (draftNotes.length < 1) {
        dispatch(getDraftNotesByDraftId(draft.id));
      }
    }

    if (proposedRule) {
      setChapterName(proposedRule?.proposedRuleName);
      setChapterNumber(proposedRule?.proposedRuleNumber);
    }

    if (cmsItems.length < 1) {
      dispatch(getAllPublicCms());
    } else {
      let guidelineDocument = cmsItems.find((cmsItem) => cmsItem.cmsItemName === 'StyleGuide');
      if (guidelineDocument) {
        setGuidelineDocumentName(guidelineDocument?.cmsItemValue!);
      }
    }

    if (noteTypes.length < 1) {
      dispatch(getDraftNoteTypes());
    }

    fetchDraftEditStatus();
  }, []);

  const [isAgencyStatusAndRole, setIsAgencyStatusAndRole] = useState<boolean>();
  const [isSosStatusAndRole, setIsSosStatusAndRole] = useState<boolean>(false);
  // const [isSosRoleAndGameAndParkTitle, setIsSosRoleAndGameAndParkTitle] = useState<boolean>(false);
  // const [isGameAndParkTitle, setIsGameAndParkTitle] = useState<boolean>(false);
  const [isValidStatus, setIsValidStatus] = useState<boolean>();

  const fetchDraftEditStatus = async () => {
    let newStatus = await draftAPI.getDraftEditStatus(draft.id);
    // newStatus
    // 0: Uneditable filings
    // 1: SOS Pending, SOS may add comments
    // 2: Agency users may edit the draft
    let userRoles: any[] = JSON.parse(sessionStorage.getItem('roles') ?? '[]');
    // console.log('userRoles - ', userRoles)
    // console.log('newStatus - ', newStatus)

    const GAME_AND_PARK_TITLE = 99;

    let isGamePark: Boolean;
    let isValid = false;
    // let isSosGamePark: Boolean;
    let isAgencyRole: Boolean;
    let isSosRole: Boolean;
    // let isSosGameAndSosPending;
    let isAgencyAndEditable;

    // check if the title is game and park (title 99)
    isGamePark = draft.titleId === GAME_AND_PARK_TITLE;

    // check if the user is SOS
    isSosRole = userRoles.some((role) => role === Roles.SosOffice);

    // is user SOS and game and park and in sos pending status
    // isSosGameAndSosPending = (isGamePark && isSosRole && newStatus === 1);

    // check if the user is agency
    isAgencyRole = userRoles.some((role) => role === Roles.Agency);

    // check if the status is editable
    isAgencyAndEditable =
      (isAgencyRole && newStatus === 2) ||
      userRoles.some((role) => role === Roles.Admin && newStatus === 2);
    console.log('Should be editable', isAgencyAndEditable);

    // console.log('isGamePark - ', isGamePark);
    // console.log('newStatus - ', newStatus);

    // console.log('isSosRole - ', isSosRole);
    // console.log('isSosGameAndSosPending - ', isSosGameAndSosPending);
    // console.log('isAgencyRole - ', isAgencyRole);
    // console.log('isAgencyAndEditable - ', isAgencyAndEditable);

    // the draft doesn't have an associated filing to it: isEditable
    // the draft does have an associated filing, but the filing is in a New status: isEditable
    // the draft does have an associated filing, the filing is in the SOS-Pending status, and the user has the SOS-Office role: isEditable

    if (isGamePark) {
      isValid = newStatus === 2 || (isSosRole && newStatus === 1);
    } else {
      isValid = isAgencyAndEditable;
    }

    // console.log('isValid - ', isValid);
    // console.log('--------------------------');

    setIsValidStatus(isValid);

    // setIsGameAndParkTitle(draft.titleId === GAME_AND_PARK_TITLE);
    setIsAgencyStatusAndRole(newStatus === 2 && userRoles.some((role) => role === Roles.Agency));
    setIsSosStatusAndRole(newStatus === 1 && userRoles.some((role) => role === Roles.SosOffice));
    // setIsSosRoleAndGameAndParkTitle(newStatus === 3 && userRoles.some(role => role === Roles.SosOffice));

    // console.log('isGameAndPark - ', draft.titleId === 99);
    // console.log('isSosAndGameAndParkAndSOS - ', newStatus === 3 && userRoles.some(role => role === Roles.SosOffice));

    // console.log('isAgencyStatusAndRole - ', newStatus === 2 && userRoles.some(role => role === Roles.Agency));
    // console.log('isSosStatusAndRole - ', newStatus === 1 && userRoles.some(role => role === Roles.SosOffice));
  };

  useEffect(() => {
    if (noteTypes.length > 1) {
      let noteList: any[] = [];
      noteTypes.forEach(function (note: any, index: number) {
        noteList.push({ ...note, text: transformUpperCamelCase(note.noteTypeName) });
      });
      setNoteTypeList(noteList);
    }
  }, [noteTypes]);

  const getChapterImages = async () => {
    let files: any = [];
    if (!!chapter) {
      files = await chapterFileAPI.getChapterFilesByChapterId(chapter.id);
    }
    if (!!proposedRule) {
      let proposedRuleFiles = await proposedRuleImageAPI.getByProposedRuleId(proposedRule?.id!);
      files = files.concat(proposedRuleFiles);
    }
    setChapterFiles(files);
  };

  useEffect(() => {
    // Fetch chapter images
    if (!proposedRule && chapter) {
      setChapterName(chapter?.chapterName);
      setChapterNumber(chapter?.chapterNumber);
    } else if (!chapter && !!proposedRule && proposedRule != null) {
      setChapterName(proposedRule?.proposedRuleName);
      setChapterNumber(proposedRule?.proposedRuleNumber);
    }

    if (!!proposedRule || !!chapter) {
      getChapterImages();
    }
  }, [proposedRule, chapter]);
  // #endregion

  // #region change handlers
  const handleChapterNameChange = (chapterName: string) => {
    setChapterName(chapterName);
  };

  const handleChapterNumberChange = (chapterNumber: string) => {
    setChapterNumber(chapterNumber);
  };
  // #endregion

  // #region save function
  const saveForm = (chapterText: any) => {
    // console.table({ text: chapterText, name: chapterName, number: chapterNumber });
    if (!!proposedRule && proposedRule !== null) {
      // update
      let newProposedRule: ProposedRule = {
        id: proposedRule.id,
        draftId: proposedRule.draftId,
        proposedRuleHtml: chapterText,
        proposedRuleName: chapterName ?? '',
        proposedRuleNumber: chapterNumber ?? '',
      };
      dispatch(updateProposedRule(newProposedRule))
        .unwrap()
        .then(() => {
          dispatch(getProposedRuleByDraftId(draft.id));
          addNotification({ type: 'success', message: `Saved successfully` });
          dispatch(getProposedRuleHistoryByDraftId(draft.id));
        })
        .catch((error: any) => {
          addNotification({
            type: 'error',
            message: `Unable to save. ${error.message}`,
          });
        });
    } else {
      // create
      let newProposedRule: ProposedRule = {
        draftId: draft.id,
        proposedRuleHtml: chapterText,
        proposedRuleName: chapterName ?? '',
        proposedRuleNumber: chapterNumber ?? '',
      };
      delete newProposedRule.id;
      dispatch(createProposedRule(newProposedRule))
        .unwrap()
        .then(() => {
          dispatch(getProposedRuleByDraftId(draft.id));
          addNotification({
            type: 'success',
            message: `Saved successfully`,
          });
          dispatch(getProposedRuleHistoryByDraftId(draft.id));
        })
        .catch((error: any) => {
          addNotification({
            type: 'error',
            message: `Unable to save. ${error.message}`,
          });
        });
    }
  };
  // #endregion

  // #region File Upload and Delete functions
  const handleFileUploadResponseMessage = (message: string, type: 'error' | 'success') => {
    addNotification({
      type: type,
      message: message,
    });
  };

  const saveFile = async (file: any) => {
    // let ext = file.blobName.split('.').pop();
    let proposedRuleImage: proposedRuleImage = {
      blobName: file.blobName,
      containerName: BlobContainer.proposedRuleImageContainer,
      proposedRuleId: proposedRule?.id!,
    };

    delete proposedRuleImage.id;

    try {
      await proposedRuleImageAPI.createProposedRuleImage(proposedRuleImage);
      addNotification({ type: 'success', message: 'File successfully added to proposed rule' });
    } catch (error: any) {
      addNotification({
        type: 'error',
        message: `File failed to save to the proposed rule: ${error.message}`,
      });
    }
  };

  const deleteFile = async (file: any) => {
    if (file[0].id) {
      proposedRuleImageAPI.deleteById(file[0].id);
    } else {
      proposedRuleImageAPI.deleteByBlobName(file[0].blobName);
    }
  };
  //#endregion

  const toggleHistory = () => {
    isHistoryOpen ? setIsHistoryOpen(false) : setIsHistoryOpen(true);
    dispatch(getProposedRuleHistoryByDraftId(draft.id));
  };

  const downloadGuidelines = () => {};

  const toggleNoteEditor = (noteType?: string) => {
    // get note type on split button selection
    if (typeof noteType === 'string' && noteTypes) {
      let note: NoteType | undefined = noteTypes.find(
        (note) => transformUpperCamelCase(note.noteTypeName) === noteType
      );
      setSelectedNoteType(note);
    }

    if (isNoteOpen) {
      // close note editor and clear the existing selected note if selected
      setIsNoteOpen(false);
      if (selectedNote) {
        setSelectedNote(null);
      }
    } else {
      setIsNoteOpen(true);
    }
  };

  const saveNote = (note: string) => {
    if (selectedNote) {
      // update draft note
      let newNote: DraftNote = { ...selectedNote, note: note };
      dispatch(updateDraftNote(newNote))
        .unwrap()
        .then(() => {
          // update redux store after success
          dispatch(setUpdatedDraftNote(newNote));
          addNotification({ type: 'success', message: 'Note updated successfully.' });
          toggleNoteEditor();
        })
        .catch((e: any) => {
          addNotification({ type: 'error', message: `Failed to update note. ${e.message}` });
        });
    } else {
      // create new draft notes
      let newNote: DraftNote = {
        draftId: draft.id!,
        noteTypeId: selectedNoteType?.id!,
        note: note,
      };
      dispatch(createDraftNote(newNote))
        .unwrap()
        .then(() => {
          addNotification({ type: 'success', message: 'New Note added successfully.' });
          toggleNoteEditor();
        })
        .catch(() => {
          addNotification({ type: 'error', message: 'Failed to create new note.' });
        });
    }
  };

  const deleteNote = (note: DraftNote) => {
    if (note) {
      dispatch(deleteDraftNote(note?.id!))
        .unwrap()
        .then(() => {
          addNotification({ type: 'success', message: 'Deleted note successfully' });
        })
        .catch(() => {
          addNotification({ type: 'error', message: 'failed to delete draftNote' });
        });
    }
  };

  const openEditDraftNote = (note: DraftNote) => {
    setSelectedNote(note);
    toggleNoteEditor();
  };

  //#region Draft Preview

  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);

  const togglePreview = () => {
    if (!isPreviewOpen) {
      getDraftPreviewHtml();
    }
    setIsPreviewOpen(!isPreviewOpen);
  };

  const [previewHtml, setPreviewHtml] = useState<any>();

  const getDraftPreviewHtml = async () => {
    setPreviewHtml(await draftAPI.getCleanedPreview(proposedRule?.draftId ?? 0));
  };

  const createHTMLDocument = (html: any) => {
    let tmp = document.implementation.createHTMLDocument();
    tmp.body.innerHTML = html;

    return parse(tmp.body.innerHTML);
  };

  //#endregion

  return {
    //variables
    baseUrl,
    // state
    chapterName,
    chapterNumber,
    chapterFiles,
    isHistoryOpen,
    guidelineDocumentName,
    noteTypeList,
    isNoteOpen,
    selectedNote,
    isPreviewOpen,
    previewHtml,
    isAgencyStatusAndRole,
    isSosStatusAndRole,
    // isGameAndParkTitle,
    // isSosRoleAndGameAndParkTitle,
    isValidStatus,
    // redux
    proposedRule,
    proposedRuleHistory,
    chapter,
    cmsItems,
    noteTypes,
    draftNotes,
    savingDraft,
    // functions
    saveForm,
    saveFile,
    deleteFile,
    handleChapterNameChange,
    handleChapterNumberChange,
    toggleHistory,
    downloadGuidelines,
    handleFileUploadResponseMessage,
    toggleNoteEditor,
    saveNote,
    openEditDraftNote,
    deleteNote,
    transformUpperCamelCase,
    togglePreview,
    createHTMLDocument,
  };
};
